import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { uploadToStorage } from './uploadToStorage';
import { useAuth } from './auth/AuthContext';
import { supabase } from './supabaseClient';
import { formatDate } from './utils/formatDate';

function MentorDashboard() {
  const { user } = useAuth();
  const [mentorId, setMentorId] = useState(null);
  const [mentorError, setMentorError] = useState(null);
  const [todaysCalls, setTodaysCalls] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [callFile, setCallFile] = useState(null);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [callStatus, setCallStatus] = useState('connected'); // 'connected' or 'not_connected'
  const [callNotes, setCallNotes] = useState('');
  const [callTag, setCallTag] = useState('');
  const [uploading, setUploading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();




  // Calculate connected calls today
  const [connectedToday, setConnectedToday] = useState(0);

  useEffect(() => {
    (async () => {
      if (!user?.email) return;
      try {
        const { data, error } = await supabase
          .from('mentors')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();
        if (!error && data?.id) {
          setMentorId(data.id);
          setMentorError(null);
        } else {
          setMentorError('Mentor profile not found. Please contact an administrator.');
        }
      } catch (error) {
        console.error('Error fetching mentor profile:', error);
        setMentorError('Failed to load mentor profile. Please try refreshing the page.');
      }
    })();
  }, [user]);

  useEffect(() => {
    // Handle selectedStudent from navigation state (coming from students page)
    if (location.state?.selectedStudent) {
      setSelectedStudent(location.state.selectedStudent);
    }
  }, [location.state]);

  useEffect(() => {
    // Clear call file when switching to not connected (since field is hidden)
    if (callStatus === 'not_connected') {
      setCallFile(null);
      setCallTag(''); // Also clear call tag for not connected calls
    }
  }, [callStatus]);

  const fetchConnectedToday = useCallback(async () => {
    if (!mentorId) return;

    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const { data, error } = await supabase
      .from('calls')
      .select('id', { count: 'exact' })
      .eq('mentor_id', mentorId)
      .eq('call_status', 'connected')
      .gte('created_at', `${today}T00:00:00.000Z`)
      .lt('created_at', `${today}T23:59:59.999Z`);

    if (!error) {
      setConnectedToday(data.length);
    } else {
      console.error('Error fetching connected calls today:', error);
    }
  }, [mentorId]);

  const fetchTodaysCalls = useCallback(async () => {
    if (!mentorId) return;

    try {
      const todayISO = new Date().toISOString();
      const { data, error } = await supabase
        .from('view_student_scheduler')
        .select('*')
        .eq('mentor_id', mentorId)
        .lte('next_due_date', todayISO)
        .order('next_due_date', { ascending: true }) // Oldest due first
        .limit(10);

      if (error) throw error;
      setTodaysCalls((data || []).filter(Boolean));
    } catch (error) {
      console.error('Failed to fetch today\'s calls:', error);
      setTodaysCalls([]); // Ensure robust fallback
      setMentorError('Failed to load call list. Please try again later.');
    }
  }, [mentorId]);

  useEffect(() => {
    if (mentorId) {
      fetchTodaysCalls();
      fetchConnectedToday();
    }
  }, [mentorId, fetchTodaysCalls, fetchConnectedToday]);

  const handleFileChange = (e, setter) => {
    setter(e.target.files[0]);
  };

  const [uploadProgress, setUploadProgress] = useState(0);

  const handleUploadCall = async () => {
    if (!selectedStudent) {
      alert('Please select a student.');
      return;
    }

    // Validation based on call status
    if (callStatus === 'connected') {
      // For connected calls, require call recording
      if (!callFile) {
        alert('Call recording is required for connected calls.');
        return;
      }
    } else if (callStatus === 'not_connected') {
      // For not connected calls, require screenshot only
      if (!screenshotFile) {
        alert('Screenshot is required when call status is "Not Connected".');
        return;
      }
    }

    // File size validation
    const maxFileSize = 50 * 1024 * 1024; // 50MB Supabase limit
    if (callFile && callFile.size > maxFileSize) {
      alert(`Call recording file is too large. Maximum size is 50MB. Your file is ${(callFile.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }
    if (screenshotFile && screenshotFile.size > maxFileSize) {
      alert(`Screenshot file is too large. Maximum size is 50MB. Your file is ${(screenshotFile.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }

    // Warn about large files
    const largeFileThreshold = 10 * 1024 * 1024; // 10MB
    if ((callFile && callFile.size > largeFileThreshold) || (screenshotFile && screenshotFile.size > largeFileThreshold)) {
      const largeFiles = [];
      if (callFile && callFile.size > largeFileThreshold) {
        largeFiles.push(`recording: ${(callFile.size / (1024 * 1024)).toFixed(2)}MB`);
      }
      if (screenshotFile && screenshotFile.size > largeFileThreshold) {
        largeFiles.push(`screenshot: ${(screenshotFile.size / (1024 * 1024)).toFixed(2)}MB`);
      }
      const proceed = window.confirm(`You are uploading large file(s): ${largeFiles.join(', ')}. This may take longer. Continue?`);
      if (!proceed) return;
    }

    setUploading(true);
    setUploadProgress(0);
    let retryCount = 0;
    const maxRetries = 3;

    const attemptUpload = async () => {
      try {
        setUploadProgress(0);

        let callObjectPath = null;
        if (callFile) {
          callObjectPath = await uploadToStorage('calls', callFile, `calls/${selectedStudent.id}/`, (loaded, total) => {
            setUploadProgress((loaded / total) * 50); // First 50% for call recording
          });
        }

        let screenshotObjectPath = null;
        if (screenshotFile) {
          screenshotObjectPath = await uploadToStorage('calls', screenshotFile, `calls/${selectedStudent.id}/`, (loaded, total) => {
            const baseProgress = callFile ? 50 : 0; // Start from 50% if call recording was uploaded
            setUploadProgress(baseProgress + (loaded / total) * 40); // Next 40% for screenshot
          });
        }

        setUploadProgress(90); // Almost done with uploads

        const { error } = await supabase
          .from('calls')
          .insert({
            student_id: selectedStudent.id,
            mentor_id: mentorId,
            call_recording_url: callObjectPath,
            screenshots_url: screenshotObjectPath,
            call_status: callStatus,
            notes: callNotes.trim() || null,
            tags: callTag || null,
          });

        if (error) throw error;

        setUploadProgress(100);

        alert('Call uploaded successfully and will appear in the student timeline.');

        // Optimistically remove student from today's list
        setTodaysCalls(prev => prev.filter(s => s.id !== selectedStudent.id));

        if (callStatus === 'connected') {
          setConnectedToday(prev => prev + 1);
        }

        // Reset form
        setCallFile(null);
        setScreenshotFile(null);
        setCallStatus('connected');
        setCallNotes('');
        setCallTag('');
        setSelectedStudent(null);

        // Refresh today's calls to apply the new scheduling logic
        fetchTodaysCalls();
      } catch (err) {
        console.error('Failed to upload call', err);

        // Determine error type and provide appropriate message
        let errorMessage = 'Error uploading call: ' + err.message;

        if (err.message?.includes('Payload too large') || err.message?.includes('413')) {
          errorMessage = 'File too large. Please ensure files are under 50MB.';
        } else if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
          errorMessage = 'Network error during upload. Please check your connection and try again.';
        } else if (err.message?.includes('timeout') || err.message?.includes('408')) {
          errorMessage = 'Upload timed out. Large files may take longer to upload.';
        } else if (err.message?.includes('403') || err.message?.includes('Unauthorized')) {
          errorMessage = 'Upload permission denied. Please contact an administrator.';
        }

        // Retry logic for network-related errors
        const isRetryableError = err.message?.includes('Failed to fetch') ||
          err.message?.includes('NetworkError') ||
          err.message?.includes('timeout');

        if (isRetryableError && retryCount < maxRetries) {
          retryCount++;
          console.log(`Upload failed, retrying... (${retryCount}/${maxRetries})`);
          setTimeout(() => attemptUpload(), 2000 * retryCount); // Exponential backoff
          return;
        }

        alert(errorMessage);
      }
    };

    await attemptUpload();
    setUploading(false);
    setUploadProgress(0);
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Mentor workspace</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Upload call recordings, share resources, and keep students engaged.
        </p>
      </header>

      {mentorError && (
        <section className="rounded-2xl border border-rose-200 bg-rose-50 p-6 dark:border-rose-400/40 dark:bg-rose-500/10">
          <h2 className="text-lg font-semibold text-rose-900 dark:text-rose-200">Profile Error</h2>
          <p className="mt-1 text-sm text-rose-700 dark:text-rose-300">{mentorError}</p>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-xl sm:text-lg font-semibold text-slate-900 dark:text-white">Today's Call List</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Connected calls today: <span className="font-semibold text-green-600 dark:text-green-400">{connectedToday}/10</span>
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Students in queue: {todaysCalls.length}
            </p>
            {todaysCalls.length > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
                Tap a student to start recording their call
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          {todaysCalls.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 px-6 py-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              <div className="text-4xl mb-4">📞</div>
              <p className="font-medium">All caught up!</p>
              <p className="mt-1">No students scheduled for calls today.</p>
              <button
                onClick={() => navigate('/mentor/students')}
                className="mt-4 px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-dark transition"
              >
                View All Students
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {todaysCalls.map((student, index) => (
                <div key={student.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedStudent(selectedStudent?.id === student.id ? null : student)}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-200 hover:border-brand/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand/40 active:scale-95 ${selectedStudent?.id === student.id
                      ? 'border-brand bg-brand/5 shadow-md rounded-b-none'
                      : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-base font-semibold text-slate-900 dark:text-white truncate">
                          {student.name || 'Unnamed student'}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-1">
                          {student.phone} • {student.unique_id || 'No EPS ID'}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                          Added {formatDate(student.created_at)}
                        </p>
                      </div>
                      <div className="ml-3 flex-shrink-0">
                        <div className={`w-4 h-4 rounded-full ${selectedStudent?.id === student.id ? 'bg-brand' : 'bg-slate-300 dark:bg-slate-600'}`}></div>
                      </div>
                    </div>
                  </button>

                  {selectedStudent && selectedStudent.id === student.id && (
                    <div className="border-2 border-t-0 border-brand bg-brand/5 rounded-b-xl p-4 sm:p-6 dark:bg-slate-800/50">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                            Call with {selectedStudent.name || 'selected student'}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            {callStatus === 'connected'
                              ? 'Upload your call recording and optional screenshot'
                              : 'Upload a screenshot of the call attempt'}
                          </p>
                        </div>
                        <button
                          onClick={() => setSelectedStudent(null)}
                          className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                          aria-label="Cancel call recording"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="space-y-6">
                        <label className="block space-y-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Call Status</span>
                          <select
                            value={callStatus}
                            onChange={(e) => setCallStatus(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                          >
                            <option value="connected">✅ Connected</option>
                            <option value="not_connected">❌ Not Connected</option>
                          </select>
                        </label>

                        {callStatus === 'connected' && (
                          <label className="block space-y-2">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Call Recording <span className="text-red-500">*</span>
                            </span>
                            <input
                              type="file"
                              accept="video/*,audio/*"
                              onChange={(e) => handleFileChange(e, setCallFile)}
                              data-testid="call-recording-input"
                              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-700 focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand-dark"
                              required
                            />
                            {callFile && (
                              <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                                ✓ {callFile.name}
                              </p>
                            )}
                            <p className="text-xs text-red-600 dark:text-red-400 mt-2">🎵 Required for connected calls</p>
                          </label>
                        )}

                        <label className="block space-y-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Screenshot {callStatus === 'not_connected' && <span className="text-red-500">*</span>}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, setScreenshotFile)}
                            data-testid="screenshot-input"
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-700 focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-500 file:text-white hover:file:bg-slate-600"
                            required={callStatus === 'not_connected'}
                          />
                          {screenshotFile && (
                            <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                              ✓ {screenshotFile.name}
                            </p>
                          )}
                          {callStatus === 'not_connected' && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-2">📷 Required for not connected calls</p>
                          )}
                        </label>

                        <label className="block space-y-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Notes</span>
                          <textarea
                            value={callNotes}
                            onChange={(e) => setCallNotes(e.target.value)}
                            placeholder="How did the call go? Any important updates or concerns?"
                            rows={4}
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 resize-none"
                          />
                        </label>

                        {callStatus === 'connected' && (
                          <label className="block space-y-2">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Call Category</span>
                            <select
                              value={callTag}
                              onChange={(e) => setCallTag(e.target.value)}
                              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            >
                              <option value="">Select category...</option>
                              <option value="refund_requested">💰 Refund Requested</option>
                            </select>
                          </label>
                        )}

                        <div className="flex gap-3 pt-4">
                          <button
                            onClick={() => setSelectedStudent(null)}
                            className="flex-1 px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 transition"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUploadCall}
                            disabled={uploading || (callStatus === 'connected' && !callFile) || (callStatus === 'not_connected' && !screenshotFile)}
                            className="flex-1 px-6 py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60 disabled:cursor-not-allowed transition active:scale-95"
                          >
                            {uploading ? '📤 Uploading...' : '📤 Upload Call'}
                          </button>
                        </div>

                        {uploading && (
                          <div className="mt-4">
                            <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2">
                              <span>Uploading files...</span>
                              <span>{uploadProgress.toFixed(0)}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2 dark:bg-slate-700">
                              <div
                                className="bg-brand h-2 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default MentorDashboard;
